import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import type { DropResult } from '@hello-pangea/dnd'

import { Status } from 'src/store/store'
import { ContentComplianceStatus, Instructor } from 'src/v3/core/types'
import { DragAndDropIcon, EditIcon, RemoveIcon } from 'src/v3/core/icons'
import { isFuzzyMatch } from 'src/v3/utilities/fuzzy-match'

let statusColors: Record<
  ContentComplianceStatus,
  'primary' | 'success' | 'error'
> = {
  new: 'primary',
  approved: 'success',
  rejected: 'error',
  updated: 'primary',
  unapproved: 'error',
}

let statusLabels: Record<ContentComplianceStatus, string> = {
  new: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  updated: 'Updated – Pending Review',
  unapproved: 'Unapproved',
}

type InstructorOption =
  | Instructor
  | {
      id: '__create__'
      name: string
      inputValue: string
      credentials?: string
    }

type InstructorPayload = { name: string; credentials?: string | null }

export type InstructorsManagementProps = {
  droppableId: string
  instructors?: Instructor[]
  complianceStatus?: ContentComplianceStatus
  complianceFeedback?: string
  availableInstructors: Instructor[]
  availableInstructorsStatus: Status
  saveStatus?: Status
  onFetchAvailableInstructors: () => void
  onSave: (instructors: { instructorID: string }[]) => void
  onSaved?: () => void
  onCreateInstructor: (payload: InstructorPayload) => Promise<Instructor>
  onUpdateInstructor: (
    instructorId: string,
    payload: InstructorPayload,
  ) => Promise<Instructor>
}

let normalize = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, ' ')

let moveItem = <T,>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number,
) => {
  let removed = items[sourceIndex]
  let withoutRemoved = items.filter((_, index) => index !== sourceIndex)
  return [
    ...withoutRemoved.slice(0, destinationIndex),
    removed,
    ...withoutRemoved.slice(destinationIndex),
  ]
}

export default function InstructorsManagement({
  droppableId,
  instructors = [],
  complianceStatus,
  complianceFeedback,
  availableInstructors,
  availableInstructorsStatus,
  saveStatus,
  onFetchAvailableInstructors,
  onSave,
  onSaved,
  onCreateInstructor,
  onUpdateInstructor,
}: InstructorsManagementProps): ReactElement {
  let [isEditing, setIsEditing] = useState(false)
  let [submitted, setSubmitted] = useState(false)
  let [confirmOpen, setConfirmOpen] = useState(false)
  let [selectedInstructors, setSelectedInstructors] =
    useState<Instructor[]>(instructors)
  let [createDialogOpen, setCreateDialogOpen] = useState(false)
  let [createName, setCreateName] = useState('')
  let [createCredentials, setCreateCredentials] = useState('')
  let [createError, setCreateError] = useState<string | null>(null)
  let [createLoading, setCreateLoading] = useState(false)
  let [editInstructor, setEditInstructor] = useState<Instructor | null>(null)
  let [editName, setEditName] = useState('')
  let [editCredentials, setEditCredentials] = useState('')
  let [editError, setEditError] = useState<string | null>(null)
  let [editLoading, setEditLoading] = useState(false)

  let isApproved = complianceStatus === 'approved'
  let isSaving = submitted && saveStatus === 'pending'
  let saveFailed = submitted && saveStatus === 'rejected'

  useEffect(() => {
    if (availableInstructorsStatus === 'uninitialized') {
      onFetchAvailableInstructors()
    }
  }, [availableInstructorsStatus, onFetchAvailableInstructors])

  useEffect(() => {
    if (!isEditing) {
      setSelectedInstructors(instructors)
    }
  }, [instructors, isEditing])

  useEffect(() => {
    if (submitted && saveStatus === 'fulfilled') {
      setSubmitted(false)
      setIsEditing(false)
      if (onSaved) onSaved()
    }
  }, [saveStatus, submitted, onSaved])

  let selectedIds = useMemo(
    () => new Set(selectedInstructors.map((i) => i.id)),
    [selectedInstructors],
  )

  let unselectedInstructors = useMemo(
    () => availableInstructors.filter((i) => !selectedIds.has(i.id)),
    [availableInstructors, selectedIds],
  )

  let startEditing = () => {
    setSelectedInstructors(instructors)
    setIsEditing(true)
  }

  let requestEdit = () => {
    if (isApproved) {
      setConfirmOpen(true)
      return
    }
    startEditing()
  }

  let handleSave = () => {
    setSubmitted(true)
    onSave(selectedInstructors.map((i) => ({ instructorID: i.id })))
  }

  let handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    setSelectedInstructors((current) =>
      moveItem(current, result.source.index, result.destination!.index),
    )
  }

  let createInstructor = async () => {
    let name = createName.trim()
    if (!name) {
      setCreateError('Name is required')
      return
    }
    let alreadyExists = availableInstructors.some(
      (i) => normalize(i.name) === normalize(name),
    )
    if (alreadyExists) {
      setCreateError(
        'An instructor with this name already exists. Search for and select them instead.',
      )
      return
    }
    setCreateLoading(true)
    setCreateError(null)
    try {
      let created = await onCreateInstructor({
        name,
        credentials: createCredentials.trim() || null,
      })
      setSelectedInstructors((current) => [...current, created])
      setCreateDialogOpen(false)
      setCreateName('')
      setCreateCredentials('')
    } catch {
      setCreateError('Failed to create instructor. Please try again.')
    } finally {
      setCreateLoading(false)
    }
  }

  let openEditInstructor = (instructor: Instructor) => {
    setEditInstructor(instructor)
    setEditName(instructor.name)
    setEditCredentials(instructor.credentials ?? '')
    setEditError(null)
  }

  let updateInstructor = async () => {
    if (!editInstructor) return
    let name = editName.trim()
    if (!name) {
      setEditError('Name is required')
      return
    }
    setEditLoading(true)
    setEditError(null)
    try {
      let updated = await onUpdateInstructor(editInstructor.id, {
        name,
        credentials: editCredentials.trim() || null,
      })
      setSelectedInstructors((current) =>
        current.map((instructor) =>
          instructor.id === updated.id ? updated : instructor,
        ),
      )
      setEditInstructor(null)
      setEditName('')
      setEditCredentials('')
    } catch {
      setEditError('Failed to update instructor. Please try again.')
    } finally {
      setEditLoading(false)
    }
  }

  let renderInstructor = (instructor: Instructor) => (
    <Box className="min-w-0">
      <Typography variant="body2" noWrap>
        {instructor.name}
      </Typography>
      {instructor.credentials && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {instructor.credentials}
        </Typography>
      )}
    </Box>
  )

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          Instructors
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isApproved && (
            <LockIcon fontSize="small" sx={{ color: 'success.main' }} />
          )}
          {complianceStatus && (
            <Chip
              size="small"
              label={statusLabels[complianceStatus]}
              variant="outlined"
              color={statusColors[complianceStatus]}
            />
          )}
        </Box>
      </Box>

      {!isEditing ? (
        <Box>
          <Box
            sx={{
              p: instructors.length === 0 ? 3 : 0,
              bgcolor:
                instructors.length === 0 ? 'background.paperDark' : undefined,
              borderRadius: 1,
              textAlign: instructors.length === 0 ? 'center' : undefined,
            }}
          >
            {instructors.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No instructors assigned yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {instructors.map((instructor, index) => (
                  <Paper key={instructor.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography color="text.secondary" variant="caption">
                      Instructor {index + 1}
                    </Typography>
                    {renderInstructor(instructor)}
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={requestEdit}
            sx={{ mt: 1.5 }}
          >
            {instructors.length === 0 ? 'Assign Instructors' : 'Manage'}
          </Button>
        </Box>
      ) : (
        <Box>
          <Autocomplete<InstructorOption>
            options={unselectedInstructors}
            value={null}
            loading={availableInstructorsStatus === 'pending'}
            disabled={isSaving}
            getOptionLabel={(option) =>
              option.id === '__create__'
                ? option.name
                : option.credentials
                  ? `${option.name} — ${option.credentials}`
                  : option.name
            }
            filterOptions={(options, params) => {
              let q = params.inputValue
              let filtered = options.filter(
                (o) =>
                  isFuzzyMatch(o.name, q) ||
                  (o.credentials ? isFuzzyMatch(o.credentials, q) : false),
              )
              let exactMatch = availableInstructors.some(
                (o) => normalize(o.name) === normalize(q),
              )
              let createOption =
                q.trim() && !exactMatch
                  ? [
                      {
                        id: '__create__' as const,
                        name: `Create "${q}"`,
                        inputValue: q,
                      },
                    ]
                  : []
              return [...filtered, ...createOption]
            }}
            onChange={(_, option) => {
              if (!option) return
              if (option.id === '__create__' && 'inputValue' in option) {
                setCreateName(option.inputValue)
                setCreateCredentials('')
                setCreateError(null)
                setCreateDialogOpen(true)
                return
              }
              setSelectedInstructors((current) => [...current, option])
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add Instructor"
                placeholder="Search by name..."
                sx={{ bgcolor: 'background.paperDark', mb: 2 }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.id === '__create__' ? (
                  <Typography
                    sx={{ color: 'primary.main', fontWeight: 'medium' }}
                  >
                    {option.name}
                  </Typography>
                ) : (
                  renderInstructor(option)
                )}
              </li>
            )}
          />

          {selectedInstructors.length === 0 ? (
            <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
              No instructors selected.
            </Typography>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId={droppableId}>
                {(provided) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    {selectedInstructors.map((instructor, index) => (
                      <Draggable
                        key={instructor.id}
                        draggableId={instructor.id}
                        index={index}
                        isDragDisabled={isSaving}
                      >
                        {(draggable) => (
                          <Paper
                            ref={draggable.innerRef}
                            {...draggable.draggableProps}
                            variant="outlined"
                            sx={{
                              p: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box
                              {...draggable.dragHandleProps}
                              aria-label={`Drag to reorder ${instructor.name}`}
                              sx={{
                                display: 'flex',
                                cursor: isSaving ? 'default' : 'grab',
                                color: 'text.secondary',
                              }}
                            >
                              <DragAndDropIcon fontSize="small" />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {renderInstructor(instructor)}
                            </Box>
                            <Tooltip title="Edit instructor">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openEditInstructor(instructor)}
                                disabled={isSaving}
                                aria-label={`Edit ${instructor.name}`}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setSelectedInstructors((current) =>
                                    current.filter(
                                      (i) => i.id !== instructor.id,
                                    ),
                                  )
                                }
                                disabled={isSaving}
                                aria-label={`Remove ${instructor.name}`}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Tooltip>
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {saveFailed && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Save failed. Please try again.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
            >
              Save Instructors
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedInstructors(instructors)
                setSubmitted(false)
                setIsEditing(false)
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {complianceFeedback &&
        (complianceStatus === 'rejected' ||
          complianceStatus === 'unapproved') && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              Compliance Feedback
            </Typography>
            <Typography variant="body2">{complianceFeedback}</Typography>
          </Alert>
        )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit Approved Field</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This field has been approved. Editing will require re-approval.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false)
              startEditing()
            }}
          >
            Edit Anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false)
          setCreateError(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Instructor</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              Only create a new instructor if they weren&apos;t found in the
              search above. Creating a duplicate makes reporting and compliance
              tracking harder.
            </Alert>
            <TextField
              label="Name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              error={!!createError}
              helperText={createError}
              required
              autoFocus
            />
            <TextField
              label="Credentials"
              value={createCredentials}
              onChange={(e) => setCreateCredentials(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false)
              setCreateError(null)
            }}
            disabled={createLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createInstructor}
            disabled={createLoading}
          >
            {createLoading ? 'Creating...' : 'Create Instructor'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editInstructor}
        onClose={() => setEditInstructor(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Instructor</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              error={!!editError}
              helperText={editError}
              required
              autoFocus
            />
            <TextField
              label="Credentials"
              value={editCredentials}
              onChange={(e) => setEditCredentials(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditInstructor(null)
              setEditError(null)
            }}
            disabled={editLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={updateInstructor}
            disabled={editLoading}
          >
            {editLoading ? 'Saving...' : 'Save Instructor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
